import { useAutoTranslation, useProcessCreationLoadingStore } from "@/entities"
import modalStyles from "../modals.module.scss"
import styles from "./styles.module.scss"
import { useEffect, useRef, useState } from "react"
import { LANG_KEYS } from "@/shared/constants"
import { Progress } from "@/components/ui/progress"
import { Confetti } from "@/components/ui/confetti"

export const ProcessCreationLoaderModal = () => {
  const infoPassed = useProcessCreationLoadingStore(s => s.infoPassed)
  const loadingLength = useProcessCreationLoadingStore(s => s.loadingLength)
  const { t } = useAutoTranslation()
  const startTime = useRef<number | null>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const [stepText, setStepText] = useState<string>(
    t(LANG_KEYS.TRANSFERING_DATA) + "..."
  )
  const [percentage, setPercentage] = useState(0)
  const [showConfetti, setShowConfetti] = useState(false)
  const hasShownConfetti = useRef(false)

  const handleClose = () => {
    if (infoPassed === "passed") {
      useProcessCreationLoadingStore.getState().closeAppLoading()
    }
  }

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        handleClose()
      }
    }

    window.addEventListener("keydown", handleKeyPress)
    return () => {
      window.removeEventListener("keydown", handleKeyPress)
    }
  }, [])

  useEffect(() => {
    if (loadingLength) {
      // Two-phase fill (no short/long split). The loader closes once the first
      // chunk is ready (note created). The first 80% fills fast at 3.024/s
      // (~20% quicker than before, reaching 80% by ~26s); the last stretch
      // crawls at 1.48/s so the 99% cap still lands at ~39s — same total time,
      // but the final 20% feels harder. Margin remains if the chunk is slow.
      const FAST_RATE = 3.024
      const SLOW_RATE = 1.48
      const KNEE_PCT = 80
      const kneeTime = KNEE_PCT / FAST_RATE
      startTime.current = Date.now()
      setPercentage(0)

      const updatePercentage = () => {
        if (startTime.current === null) return

        const secondsPassed = Math.floor(
          (Date.now() - startTime.current) / 1000
        )
        const rawPercentage =
          secondsPassed <= kneeTime
            ? secondsPassed * FAST_RATE
            : KNEE_PCT + (secondsPassed - kneeTime) * SLOW_RATE
        const newPercentage = Math.min(99, +rawPercentage.toFixed(2))

        setPercentage(newPercentage)

        if (newPercentage >= 99 && intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      }

      intervalRef.current = setInterval(updatePercentage, 1000)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [loadingLength])

  // Trigger confetti when upload is successfully passed
  useEffect(() => {
    if (infoPassed === "passed" && !hasShownConfetti.current) {
      hasShownConfetti.current = true
      setShowConfetti(true)
    }
  }, [infoPassed])

  useEffect(() => {
    if (infoPassed === "started") {
      setStepText(t(LANG_KEYS.TRANSFERING_DATA) + "...")
      return
    } else if (infoPassed === "passed" && !loadingLength) {
      setStepText(t(LANG_KEYS.PROCESSING_TEXT))
    } else if (infoPassed === "passed" && loadingLength) {
      if (percentage < 20) {
        setStepText(t(LANG_KEYS.STARTING_CONVERSION))
      } else if (percentage >= 20 && percentage < 40) {
        setStepText(t(LANG_KEYS.SETTING_UP_YOUR_CONTENT_FOR_CONVERSION))
      } else if (percentage >= 40 && percentage < 60) {
        setStepText(t(LANG_KEYS.RETRIEVING_CONTENT_AND_PREPARING_AUDIO))
      } else if (percentage >= 60 && percentage < 80) {
        setStepText(
          t(LANG_KEYS.ENHANCING_QUALITY_FOR_THE_BEST_LISTENING_EXPERIENCE)
        )
      } else if (percentage >= 80) {
        setStepText(t(LANG_KEYS.WE_RE_ALMOST_DONE_JUST_A_BIT_MORE_TO_GO))
      }
    }
  }, [percentage, loadingLength, infoPassed])

  return (
    <>
      <Confetti
        trigger={showConfetti}
        onComplete={() => setShowConfetti(false)}
      />
      <div className={modalStyles.backdrop} onClick={handleClose}>
        <div className={styles.wrapper}>
          <div className={styles.card} onClick={e => e.stopPropagation()}>
            <h1 className={styles.card__title}>{stepText}</h1>

            <div className={styles.card__percentageWrapper}>
              {loadingLength ? `${Math.round(percentage)}%` : null}
            </div>
            <Progress
              value={percentage}
              indeterminate={!loadingLength || infoPassed !== "passed"}
            />
            <h3 className={styles.card__description}>
              {infoPassed === "passed"
                ? "Conversion in progress - processing in the background. You can close the website."
                : "Preparing to convert your text into audio. Please keep the website open."}
            </h3>
          </div>
        </div>
      </div>
    </>
  )
}
